import { useState, useEffect, useCallback } from 'react';
import { authenticatedAPI } from '../utils/authApi';

export const useDevices = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(new Set());

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authenticatedAPI.get('devices', '/devices');
      setDevices(response.devices || []);
    } catch (error) {
      const errorMessage = error.message || 'Failed to fetch devices';
      setError(errorMessage);
      console.error('Error fetching devices:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const connectFitbit = useCallback(async () => {
    try {
      const response = await authenticatedAPI.post('devices', '/devices/fitbit/authorize');
      
      if (response.authorize_url) {
        // Open Fitbit authorization in new window
        const authWindow = window.open(
          response.authorize_url, 
          'fitbit-auth', 
          'width=600,height=600,scrollbars=yes,resizable=yes'
        );
        
        // Return a promise that resolves when auth completes
        return new Promise((resolve, reject) => {
          const checkAuth = setInterval(async () => {
            if (authWindow.closed) {
              clearInterval(checkAuth);
              try {
                await fetchDevices();
                resolve(true);
              } catch (error) {
                reject(error);
              }
            }
          }, 1000);
          
          // Timeout after 5 minutes
          setTimeout(() => {
            clearInterval(checkAuth);
            if (!authWindow.closed) {
              authWindow.close();
            }
            reject(new Error('Authentication timeout'));
          }, 300000);
        });
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error) {
      console.error('Fitbit connection error:', error);
      throw error;
    }
  }, [fetchDevices]);

  const connectGarmin = useCallback(async (credentials) => {
    try {
      const response = await authenticatedAPI.post('devices', '/devices/garmin/login', {
        body: {
          username: credentials.username,
          password: credentials.password
        }
      });

      if (response.success) {
        await fetchDevices();
        return response;
      } else {
        throw new Error('Garmin connection failed');
      }
    } catch (error) {
      console.error('Garmin connection error:', error);
      throw error;
    }
  }, [fetchDevices]);

  const syncDevice = useCallback(async (deviceType) => {
    setSyncing(prev => new Set([...prev, deviceType]));
    
    try {
      const response = await authenticatedAPI.post('devices', `/devices/${deviceType}/sync`, {
        body: {
          challengeId: 'current',
          dateRange: {
            startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0]
          }
        }
      });

      if (response.success) {
        // Update device last sync time
        setDevices(prev => prev.map(device => 
          device.device_type === deviceType 
            ? { 
                ...device, 
                last_sync: new Date().toISOString(),
                connection_info: {
                  ...device.connection_info,
                  last_sync: new Date().toISOString()
                }
              }
            : device
        ));
        return response;
      } else {
        throw new Error('Sync failed');
      }
    } catch (error) {
      console.error(`${deviceType} sync error:`, error);
      // Update device status to show error
      setDevices(prev => prev.map(device => 
        device.device_type === deviceType 
          ? { 
              ...device, 
              connection_info: {
                ...device.connection_info,
                device_specific: {
                  ...device.connection_info?.device_specific,
                  token_valid: false
                }
              }
            }
          : device
      ));
      throw error;
    } finally {
      setSyncing(prev => {
        const newSet = new Set(prev);
        newSet.delete(deviceType);
        return newSet;
      });
    }
  }, []);

  const disconnectDevice = useCallback(async (deviceType) => {
    try {
      await authenticatedAPI.del('devices', `/devices/${deviceType}`);
      await fetchDevices();
    } catch (error) {
      console.error(`${deviceType} disconnect error:`, error);
      throw error;
    }
  }, [fetchDevices]);

  const getDeviceStatus = useCallback((device) => {
    if (!device.connected) return 'disconnected';
    if (device.connection_info?.device_specific?.token_valid === false) return 'error';
    return 'connected';
  }, []);

  const getStatusText = useCallback((device) => {
    const status = getDeviceStatus(device);
    if (status === 'connected') return 'Connected';
    if (status === 'error') return 'Connection Error';
    return 'Not Connected';
  }, [getDeviceStatus]);

  const connectedDevices = devices.filter(d => d.connected);
  const connectionPercentage = devices.length > 0 ? Math.round((connectedDevices.length / devices.length) * 100) : 0;

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  return {
    devices,
    loading,
    error,
    syncing,
    connectedDevices,
    connectionPercentage,
    fetchDevices,
    connectFitbit,
    connectGarmin,
    syncDevice,
    disconnectDevice,
    getDeviceStatus,
    getStatusText,
    setError
  };
};

export default useDevices;